import React, { useEffect, useState } from "react";
import { ui, type Lang } from "./shared.js";

// UX-COPY-1 — a single, reusable, non-blaming permission-denial bar.
// Event-driven (mirrors DegradedModeBanner): any denial site calls
// notifyDenied("<i18n key>") and this bar shows the plain-language message
// (what / why / what to do next) localized via ui(). Auto-dismisses; clickable.
// Tone: "this is how access works, here's what you can do" — never "you erred".

const DENIAL_EVENT = "studio:access-denied";
const DISMISS_MS = 9000;          // founder: keep it 5–10s, not the old 7s
const HANDOFF_MS = 1500;          // window for a freshly-mounted toast to claim a just-fired denial

// Module-level memory of the last denial. The toast is mounted per render-branch
// (editor / pending / landing), so a denial that fires DURING navigation — e.g.
// the WS 4403 handler calls notifyDenied() then goBack(), unmounting the editor
// branch and mounting the landing branch — would be lost: the old branch's
// listener is gone and the new branch mounts AFTER the event already dispatched.
// Persisting it here lets the newly-mounted branch pick it up on mount. This was
// the root of "#3 too short" (message vanished on the editor→landing swap) and
// "#5 no notification" (unshare → goBack → the landing toast never saw the event).
let lastDenial: { messageKey: string; at: number } | null = null;

// Dedupe window: a single access-loss can be detected by more than one path in
// quick succession (the live WS close AND the autosave-failure, or a navigation
// refetch). Without this they stack into the "two notices, first correct then
// wrong" the founder saw. Suppress an identical key fired within this window.
const DEDUPE_MS = 5000;

/** Fire a denial message from anywhere (delete handlers, WS close, load fail). */
export function notifyDenied(messageKey: string): void {
  const now = Date.now();
  if (lastDenial && lastDenial.messageKey === messageKey && now - lastDenial.at < DEDUPE_MS) return;
  lastDenial = { messageKey, at: now };
  try { window.dispatchEvent(new CustomEvent(DENIAL_EVENT, { detail: { messageKey } })); }
  catch { /* no window (SSR) — ignore */ }
}

export function AccessDenialToast({ lang }: { lang: Lang }) {
  const [messageKey, setMessageKey] = useState<string | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const show = (key: string) => {
      setMessageKey(key);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setMessageKey(null), DISMISS_MS);  // auto-dismiss
    };
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { messageKey?: string } | undefined;
      if (!detail?.messageKey) return;
      show(detail.messageKey);
    };
    window.addEventListener(DENIAL_EVENT, handler);
    // Claim a denial that fired during the navigation that mounted this branch.
    if (lastDenial && Date.now() - lastDenial.at < HANDOFF_MS) show(lastDenial.messageKey);
    return () => { window.removeEventListener(DENIAL_EVENT, handler); if (timer) clearTimeout(timer); };
  }, []);

  if (!messageKey) return null;
  return (
    <div
      onClick={() => setMessageKey(null)}
      title={lang === "zh" ? "点击关闭" : "Click to dismiss"}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 10000,
        background: "#0f1a12", borderBottom: "1px solid #2a5a3a",
        color: "#9ad8b0", padding: "8px 16px", fontSize: 12,
        fontFamily: "Space Mono, monospace", textAlign: "center", cursor: "pointer",
      }}
    >
      {ui(messageKey, lang)}
    </div>
  );
}
